import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { Layout, Skeleton, Menu, Tooltip } from 'antd'
import { useSize, useMap } from 'ahooks'
import clsx from 'clsx'
import { NEW_SYSTEM_BASE_PATH, BROKER_MOST_USED_MENUS, BROKER_MOST_USED_MAX, HOME_ROOT_MENU_ID, HOME_ROOT_MENU_KEY } from '../config'
import { setTitleInfo, setIconInfo, isLinkInNewSystem } from './utlils'
import 'react-custom-scroll/dist/customScroll.css'
import styles from './sidemenu.module.less'
import { Link } from 'react-router-dom'
import localForage from 'localforage'
import { getMenu } from '@/services/api'
import { MenuNode } from '@/services/api.d'

type MostUsedMenuItem = [number, number]

const store = localForage.createInstance({ name: 'mendian-main' })
const theme = location.hostname === 'yfyk.youfangyouke.com' ? 'dark' : 'light'
const targetKeyMap: Record<string, number | undefined> = Object.create(null)

const isHomeMenu = (menu: MenuNode) => menu.id === HOME_ROOT_MENU_ID && menu.title === '首页'

const isParentMenu = (menu: MenuNode, id: number) => {
  if (menu.id === id) return true
  if (Array.isArray(menu.sublist) && menu.sublist.length) {
    for (const sub of menu.sublist) {
      if (isParentMenu(sub, id)) {
        return true
      }
    }
  }
  return false
}

const getMostUsedItems = (menuIds: number[], menuList: MenuNode[]) => {
  const result: MenuNode[] = []
  if (!Array.isArray(menuIds) ||
    menuIds.length === 0 ||
    !Array.isArray(menuList) ||
    menuList.length === 0
  ) {
    return result
  }
  for (const item of menuList) {
    const { sublist, ...rest } = item
    if (menuIds.includes(item.id) && result.every(x => x.id !== item.id)) {
      result.push({ ...rest, icon: '', target: 0 })
    } else if (Array.isArray(sublist) && sublist.length) {
      const list = getMostUsedItems(menuIds, sublist)
      result.push(...list.filter(x => result.every(y => y.id !== x.id)))
    }
  }
  return result
}

// 获取最近使用菜单
const getMostUsedMenus = async (menuList: MenuNode[]): Promise<MenuNode[]> => {
  if (menuList.length === 0) return []
  let menuIds: number[] = []
  try {
    const value = await store.getItem<string>(BROKER_MOST_USED_MENUS)
    if (value) {
      menuIds = value.split(',').map(x => Number(x.split(':')[0]))
    }
  } catch (err) {
    return []
  }
  if (menuIds.length === 0) return []
  menuIds = menuIds.splice(0, BROKER_MOST_USED_MAX)
  const menus = getMostUsedItems(menuIds, menuList)

  // 排序
  menus.sort((x, y) => menuIds.indexOf(x.id) - menuIds.indexOf(y.id))
  // 处理二级/三级菜单重名问题: 添加一级菜单前缀
  menus.forEach(child => {
    const parent = menuList.find(parent => !parent.isHomeMenu && isParentMenu(parent, child.id))
    if (parent?.title &&
      !child?.title?.includes(parent?.title)
    ) {
      child.title = child.title + '-' + parent?.title
    }
  })
  return menus
}

const fixMenuError = (menus: MenuNode[]) => {
  const host = `//${location.host}`
  menus.forEach(menu => {
    // 空格处理
    if (typeof menu.link === 'string') {
      menu.link = menu.link.trim()
      // 全地址处理
      if (menu.link.includes(host)) {
        menu.link = menu.link.replace(host, '')
        menu.link = menu.link.substring(menu.link.indexOf('/'))
      }
    }
    if (Array.isArray(menu.sublist) && menu.sublist.length) {
      fixMenuError(menu.sublist)
      const link = `m${menu.id}m`
      if (menu.link !== link) menu.link = link
    }
  })

  // 排除: 不存在子节点的父节点
  menus = menus.filter(x => !(
    x.type === 1 &&
    x.link === `m${x.id}m` &&
    (!Array.isArray(x.sublist) || x.sublist.length === 0)
  ))
}

const useMenus = () => {
  const [loading, setLoading] = useState(true)
  const [menus, setMenus] = useState<MenuNode[]>([])
  const [mostUsedMenus, setMostUsedMenus] = useState<MenuNode[]>([])
  const [rootSubMenuKeys, setRootSubMenuKeys] = useState<string[]>([])
  // region path -> path key 的快速所有，方便获取所在分支的所有菜单的key用来做open状态管理
  const [pathKeyMap, setPathKeyMap] = useState<{ [path: string]: string[] }>({})
  const [titleMap, setTitleMap] = useState<{ [path: string]: string }>({})
  const pathKeyHash: { [path: string]: string[] } = {}
  const tmpTitleMap: { [path: string]: string } = {}
  const currentKeyArr: string[] = []
  const getMenuKeyFromPath = (menus: MenuNode[]) => {
    menus.forEach(menu => {
      if (!menu.isMostUsedMenu) {
        if (menu.link === `m${menu.id}m`) {
          if (Array.isArray(menu.sublist) && menu.sublist.length) {
            currentKeyArr.push(menu.link)
            getMenuKeyFromPath(menu.sublist)
          }
        } else {
          const key = getPathName(menu.link)
          pathKeyHash[key] = currentKeyArr.concat([key])
          tmpTitleMap[key] = menu.title
          targetKeyMap[key] = menu.target
        }
      }
    })
    currentKeyArr.pop()
  }
  // endregion

  const handleHome = useCallback(async (menuList: MenuNode[]) => {
    const menu = menuList.find(x => isHomeMenu(x))
    if (menu) {
      menu.isHomeMenu = true
      const sublist: MenuNode[] = menu.sublist ?? []
      const mostUsedMenus = await getMostUsedMenus(menuList)
      setMostUsedMenus(mostUsedMenus)
      const item = sublist.find(x => x.isMostUsedMenu)
      if (item) {
        item.sublist = mostUsedMenus
      } else {
        const id = Date.now()
        sublist.push({
          id,
          title: '近期常用功能',
          link: `m${id}m`,
          target: 0,
          type: 1,
          sublist: mostUsedMenus,
          isMostUsedMenu: true
        } as MenuNode)
      }
      menu.sublist = sublist
    }
  }, [])

  // 添加菜单ID到最近使用, 最多保存10条
  const addMostUsedMenu = useCallback((menu: MenuNode) => {
    window.loggerAction?.sendnpv?.({
      action: 'left_menu_click',
      ep: { link: menu.link }
    })
    if (!menu.link || isHomeMenu(menu)) return
    // 更新近期常用功能
    const i = mostUsedMenus.findIndex(x => x.id === menu.id)
    if (i !== -1) {
      if (i > 0) {
        const e = mostUsedMenus.splice(i, 1)
        mostUsedMenus.unshift(...e)
      }
    } else {
      const root = menus.find(root => !root.isHomeMenu && isParentMenu(root, menu.id))
      const item: MenuNode = {
        title: menu.title,
        link: menu.link,
        id: menu.id,
        type: menu.type,
        target: 0
      }
      if (root?.title &&
        !item?.title?.includes(root?.title)
      ) {
        item.title = item.title + '-' + root?.title
      }
      mostUsedMenus.unshift(item)
    }
    if (mostUsedMenus.length > BROKER_MOST_USED_MAX) {
      mostUsedMenus.pop()
    }
    setMostUsedMenus(mostUsedMenus)

    // 存储格式: 菜单1:时间1,菜单2:时间2,菜单3:时间3
    // 说明一下: 此处保存时间戳，防止常用功能后续有时间上的要求
    store.getItem<string>(BROKER_MOST_USED_MENUS).then((value) => {
      const now = Date.now()
      const item = `${menu.id}:${now}`
      if (value) {
        const set = new Set<number>()
        let list = value.split(',').map(x => {
          const [id, time] = x.split(':')
          return [Number(id), Number(time)] as MostUsedMenuItem
        }).filter(x => {
          if (set.has(x[0])) return false
          set.add(x[0])
          return x[0] !== menu.id
        })
        list.unshift([menu.id, now])
        if (list.length > BROKER_MOST_USED_MAX) {
          list = list.splice(0, BROKER_MOST_USED_MAX)
        }
        store.setItem(BROKER_MOST_USED_MENUS, list.map(x => x.join(':')).join(','))
      } else {
        store.setItem(BROKER_MOST_USED_MENUS, item)
      }
    })
  }, [menus, mostUsedMenus])

  useEffect(() => {
    getMenu().then(async (res) => {
      const { entrances = [] } = res?.data || []
      fixMenuError(entrances)
      await handleHome(entrances)
      setMenus(entrances)
      setRootSubMenuKeys(entrances.map((entry: any) => entry.link))
      getMenuKeyFromPath(entrances)
      setPathKeyMap(pathKeyHash)
      setTitleMap(tmpTitleMap)
    }).finally(() => {
      setLoading(false)
    })
  }, [])

  return {
    loading,
    menus,
    rootSubMenuKeys,
    pathKeyMap,
    titleMap,
    addMostUsedMenu
  }
}

/**
 * 获取pathname部分
 * @param link
 * @returns
 */
const getPathName = (link: string) => {
  let path = link.split('?')[0].trim()
  const host = `//${location.host}`
  if (path.includes(host)) {
    path = path.replace(host, '')
    return path.substring(path.indexOf('/'))
  }
  return path
}

const iconFontClass = clsx('iconfont', styles.menuIcon)
const iconFontWbClass = clsx('iconfont-wb', styles.menuIcon)

/**
 * 新系统的链接跳转到solar的路由中
 * @param entry
 */
const getCorrectLink = (entry: MenuNode) => {
  if (isLinkInNewSystem(entry) && !entry.link.includes('/solar')) {
    return entry.link.replace(`/${NEW_SYSTEM_BASE_PATH}`, '/solar/metis')
  }
  return entry.link
}

/**
 * 解析icon，适配iconfont以及58iconfont
 * @param icon
 */
const parseIcon = (icon: string | undefined): { iconString: string; isWb: boolean } => {
  if (!icon) return { iconString: '', isWb: false }
  const isWbIcon = icon.indexOf('wb_') === 0
  return {
    iconString: isWbIcon ? icon.slice(3) : icon,
    isWb: isWbIcon
  }
}

interface SideMenuProps {
  className?: string;
}

const MetisSideMenu = (props: SideMenuProps) => {
  const ref = useRef(null)
  const size = useSize(ref)
  const [firstSelected, setFirstSelected] = useState<MenuNode>()
  const [firstActiveKey, setFirstActiveKey] = useState<string>('')
  const [firstHovered, setFirstHovered] = useState<MenuNode>()
  const [, { set: setTimerId, get: getTimerId }] = useMap<number, number>([])
  const [, { set: setActiveKey, get: getActiveKey }] = useMap<number, string>([])
  const { loading, menus, pathKeyMap, titleMap, addMostUsedMenu } = useMenus()

  const getMenuNodes = useCallback((entry: MenuNode) => {
    const iconParse = parseIcon(entry.icon)
    if (Array.isArray(entry.sublist)) {
      if (entry.sublist.length === 0) return null
      const title = (
        <div className={styles.titleWrap}>
          {entry.icon && (
            <span
              className={iconParse.isWb ? iconFontWbClass : iconFontClass}
              dangerouslySetInnerHTML={{ __html: iconParse.iconString }}
            />
          )}
          <span className={styles.title}>{entry.title}</span>
        </div>
      )
      const subMenus = entry.sublist.map(subNode => getMenuNodes(subNode))
      return (
        <Menu.SubMenu key={entry.link} title={title}>
          {subMenus}
        </Menu.SubMenu>
      )
    } else {
      return (
        <Menu.Item key={getPathName(entry.link)} active>
          {isLinkInNewSystem(entry)
            ? (
                <Link className={styles.titleWrap} to={getCorrectLink(entry)} onClick={() => addMostUsedMenu(entry)} referrerPolicy='origin'>
                  {entry.icon && (
                    <span
                      className={iconParse.isWb ? iconFontWbClass : iconFontClass}
                      dangerouslySetInnerHTML={{ __html: iconParse.iconString }}
                    />
                  )}
                  <span className={styles.title}>{entry.title}</span>
                </Link>
              )
            : (
                // eslint-disable-next-line react/jsx-no-target-blank
                <a
                  href={entry.link}
                  className={styles.titleWrap}
                  target={entry.target === 1 ? '_blank' : '_self'}
                  onClick={() => addMostUsedMenu(entry)}
                >
                  {entry.icon && (
                    <span
                      className={iconParse.isWb ? iconFontWbClass : iconFontClass}
                      dangerouslySetInnerHTML={{ __html: iconParse.iconString }}
                    />
                  )}
                  <span className={styles.title}>{entry.title}</span>
                </a>
              )
          }
        </Menu.Item>
      )
    }
  }, [])

  useEffect(() => {
    // 路由变化时更新菜单展开和选中状态
    if (!loading && menus.length > 0 && location.pathname) {
      let pathname = location.pathname.replace(
        '/solar/metis',
        '/metis'
      )
      if (pathname === '/metis/manager-recommend') {
        pathname = '/storeRecomm/recommendView'
      }
      const currentOpenedPath = pathKeyMap[pathname]
      const routerPath = pathname
      setTitleInfo(titleMap[routerPath])
      if (Array.isArray(currentOpenedPath)) {
        const [key1, key2, key3] = currentOpenedPath
        if (key1) {
          setFirstActiveKey(key1)
          updateFirstSelect(key1)
          openFirstMenu(key1, key2, key3)
        }
      } else {
        // 默认展开首页
        setFirstActiveKey(HOME_ROOT_MENU_KEY)
        updateFirstSelect(HOME_ROOT_MENU_KEY)
      }
    }
  }, [loading, menus, pathKeyMap, titleMap])

  const otherActiveMenu = useMemo(() => {
    const selected = firstHovered || firstSelected
    if (!selected ||
      !Array.isArray(selected.sublist) ||
      selected.sublist.length === 0
    ) {
      return undefined
    }
    return selected
  }, [firstSelected, firstHovered])

  const updateFirstSelect = useCallback((key: string) => {
    const selected = menus.find(x => x.link === key)
    if (selected) {
      setFirstSelected(selected)
    }
  }, [menus])

  useEffect(() => {
    setIconInfo()
  }, [])

  const openFirstMenu = useCallback(async (key1: string, key2?: string, key3?: string) => {
    const selected = menus.find(x => x.link === key1 && Array.isArray(x.sublist) && x.sublist.length)
    if (selected) {
      setFirstSelected(selected)
      const key = key3 || key2
      key && setActiveKey(selected.id, key)
    }
  }, [menus])

  const onRootEnter = useCallback((item?: MenuNode) => {
    const timerId = window.setTimeout(() => {
      setFirstHovered(item)
    }, 400)
    if (item?.id) {
      setTimerId(item.id, timerId)
    }
  }, [])

  const onRootLeave = useCallback((item: MenuNode) => {
    const timerId = getTimerId(item.id)
    if (timerId) {
      window.clearTimeout(timerId)
      setTimerId(item.id, 0)
    }
  }, [])

  const checkMenuSelect = useCallback((menu: MenuNode, key: string) => {
    if (!menu) return
    if (menu.link === key) {
      // 判断是否当前tab打开
      if (menu?.target === 0) {
        if (firstSelected) {
          setActiveKey(firstSelected.id, key)
          setFirstActiveKey(firstSelected.link)
        }
      }
    } else if (Array.isArray(menu.sublist) && menu.sublist.length) {
      for (const x of menu.sublist) {
        checkMenuSelect(x, key)
      }
    }
  }, [firstSelected])

  const roots = useMemo(() => {
    const needTip = size?.width ? size.width < 68 : false
    return menus.map((entry) => {
      const iconParse = parseIcon(entry.icon)
      return (
        <Menu.Item
          key={getPathName(entry.link)}
          onMouseEnter={() => onRootEnter?.(entry)}
          onMouseLeave={() => onRootLeave?.(entry)}
        >
          <div className={clsx(styles.titleWrap, { [styles.active]: firstHovered?.id === entry.id })}>
            {entry.icon && (
              needTip && (!Array.isArray(entry.sublist) || entry.sublist.length === 0)
                ? <Tooltip placement="right" title={entry.title}>
                  <span
                    className={iconParse.isWb ? iconFontWbClass : iconFontClass}
                    dangerouslySetInnerHTML={{ __html: iconParse.iconString }}
                  />
                </Tooltip>
                : <span
                    className={iconParse.isWb ? iconFontWbClass : iconFontClass}
                    dangerouslySetInnerHTML={{ __html: iconParse.iconString }}
                />
            )}
            <span className={styles.title}>{entry.title}</span>
          </div>
        </Menu.Item>
      )
    })
  }, [menus, firstHovered, size?.width, onRootEnter, onRootLeave])

  return (
    <Layout.Sider theme={theme} className={clsx(props.className, styles.container, styles.isNew)}>
      <Skeleton
        title={false}
        paragraph={{ rows: 22 }}
        active
        loading={menus.length === 0}
      >
        <div className={styles.menuMask}></div>
        <div className={styles.menuLayout} onMouseLeave={() => onRootEnter(undefined)}>
          <div ref={ref} className={styles.firstLevelMenus}>
            <Menu
              mode="inline"
              theme={theme}
              selectedKeys={[firstActiveKey]}
              className={styles.menuRoot}
            >
              { roots }
            </Menu>
          </div>
          <div className={styles.otherLevelMenus}>
            <div className={styles.firstLevelName}>{otherActiveMenu?.title}</div>
            {
              menus.map((item) => {
                const selectedKeys: string[] = []
                const activeKey = getActiveKey(item.id)
                if (activeKey) selectedKeys.push(activeKey)
                return (
                  <Menu
                    key={item.id}
                    className={clsx({ [styles['is-hide']]: item.id !== otherActiveMenu?.id })}
                    mode="inline"
                    forceSubMenuRender={true}
                    defaultOpenKeys={item.sublist?.map(x => x.link)}
                    selectedKeys={selectedKeys}
                    onSelect={({ key }) => {
                      if (targetKeyMap[key] !== 1) {
                        setActiveKey(item.id, key as string)
                        setFirstActiveKey(item.link)
                        setFirstSelected(item)
                      }
                    }}
                  >
                    { item.sublist?.map(entry => getMenuNodes(entry)) }
                  </Menu>
                )
              })
            }
          </div>
        </div>
      </Skeleton>
    </Layout.Sider>
  )
}

export { MetisSideMenu as NewMetisSideMenu }
